<template>
  <div class="map-container">
    <div class="cities-map">
      <svg
        :width="width"
        :height="height - 30"
        :viewBox="`0 30 ${width + 10} ${height - 30}`"
      >
        <path class="cities-map__landmass" :d="worldmapD" />
        <a
          v-for="(node, i) in nodes"
          :key="node.id"
          :href="node.url"
          class="cities-map__node"
          :title="node.label"
          @mouseenter="hoveredNodeID = node.id"
          @mouseleave="hoveredNodeID = null"
        >
          <circle r="1.25" :fill="node.color" :cx="node.x" :cy="node.y" />
        </a>
        <g
          v-if="activeNode"
          class="cities-map__popup"
          :transform="`translate(${activeNode.x} ${activeNode.y - 2.25})`"
        >
          <path
            class="cities-map__triangle"
            d="M0 0L0 -5L5 -5Z"
            :fill="activeNode.color"
          />
          <g :transform="`translate(${activeTooltipOffset} 0)`">
            <rect
              :x="(textWidth / 2) * -1"
              y="-9.5"
              :width="textWidth + textPadding * 2"
              height="7"
              :fill="activeNode.color"
            />
            <text ref="nodeLabel" :x="textPadding" y="-4.5">
              {{ activeNode.label }}
            </text>
          </g>
        </g>
      </svg>
    </div>
    <div class="map-overlay"></div>
  </div>
</template>

<script>
import axios from "axios";
import worldmapD from "../lib/worldmap.js";

export default {
  data() {
    return {
      worldmapD,
      cities: null,
      events: null,
      width: 204,
      height: 130,
      hoveredNodeID: null,
      textWidth: null,
      textPadding: 2,
    };
  },
  props: {
    type: {
      type: String, // cities, events
      required: false,
      default: "cities",
    },
  },

  created() {
    this.fetchCities();
    if (this.type === "events") this.fetchEvents();
  },

  computed: {
    // {id: number; label: string; url: string; color: string;}[]
    nodes() {
      switch (this.type) {
        case "cities":
          return (this.cities || []).map((city) => ({
            id: city.id,
            label: city.name,
            // spread x, y properties
            ...this.getCoords(city.latitude, city.longitude),
            url: `/cities/${city.abbreviation}`,
            color: city.color,
          }));
        case "events":
          let chaptersUsed = []
          return (this.events || []).map((event) => {
            const chapter = (this.cities || []).find(
              (city) => city.id === event.chapter_id
            );
            if (chaptersUsed.includes(event.chapter_id)) {
              return null;
            } else {
              chaptersUsed.push(event.chapter_id);
            }
            if (!chapter) return null;

            return {
              id: event.id,
              label: `${chapter.name}: ${new Date(event.starts_at).toLocaleDateString(
                "en-US",
                {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                }
              )}`,
              // spread x, y properties
              ...this.getCoords(chapter.latitude, chapter.longitude),
              url: `/talks/${event.slug}`,
              color: chapter.color,
            };
          }).filter(Boolean);
      }
    },
    activeNode() {
      return this.nodes.find((node) => node.id === this.hoveredNodeID);
    },
    activeTooltipOffset() {
      if (this.activeNode) {
        const halfTooltipWidth = this.textWidth / 2 + this.textPadding;
        const overflowLeft = Math.min(0, this.activeNode.x - halfTooltipWidth);
        const overflowRight = Math.max(0, this.activeNode.x + halfTooltipWidth - this.width);

        return -1 * (overflowLeft + overflowRight);
      }
    }
  },

  watch: {
    activeNode(next, prev) {
      this.$nextTick(() => {
        if (this.$refs.nodeLabel) {
          this.textWidth = this.$refs.nodeLabel.getBBox().width;
        }
      });
    },
  },

  methods: {
    fetchCities() {
      // cities: [{"id":148,"name":"Aarhus","abbreviation":"ARH","color":"#193941","longitude":10.203921,"latitude":56.162939}]
      axios.get("/cities.json").then((response) => {
        this.cities = response.data;
      });
    },
    fetchEvents() {
      // events: [{
      //   "id": 17525,
      //   "chapter_id": 148,
      //   "slug": "show-me-how-to-restart-this-chapter",
      //   "starts_at": "2030-12-31T13:30:00.000Z",
      //   "ends_at": "2030-12-31T15:00:00.000Z",
      //   "name": "Show me how to restart this chapter",
      //   "description": "If you’re serious about bringing CreativeMornings to your town, we’d love to hear from you. \r\n\r\nFor full details about hosting a CreativeMornings chapter in your city, review our \u003Ca href=\"https://creativemornings.com/pages/start-a-chapter\" target=\"new\"\u003EStart a Chapter\u003C/a\u003E page and start your journey there!\r\n\r\n\u003Ch2\u003EWhat usually happens at a CreativeMornings?\u003C/h2\u003E\r\n\r\n\u003Cp\u003EMagic.\u003Cbr /\u003E\r\n\u003Cbr /\u003E\r\nRegistration for each event opens at the beginning of the week on the chapter’s CreativeMornings.com page.\r\n\r\nTickets are free, but you have to be quick because many of our events fill up fast.\r\n\r\nIf you get a ticket, you can show up (events are usually on Friday morning) and enjoy free breakfast, meet your local community, and hear an inspiring 20-minute talk from a neighbor that’s related to the monthly global theme.\r\n\r\nAfter the talk, there’s usually a Q&A and then you’re out of the door by 10 AM, inspired to pass the feelings forward.\r\n\r\n\u003Cimg src=\"https://mcusercontent.com/b8d0c821758ac4979998d6301/images/77601c6b-9abb-9439-8ea7-29932d04e7ca.jpg\"\u003E\u003Cbr /\u003E\r\nPhoto credit: Tora Chirila from CreativeMornings/Montreal.\r\n\r\n\u003Cimg src=\"https://mcusercontent.com/b8d0c821758ac4979998d6301/images/c577566c-58df-5866-3be3-1c8c4a6a1caa.jpg\"\u003E\u003Cbr /\u003E\r\n Photo credit, top left: Grzegorz Stadnik from CreativeMornings/Wroclaw. Top right: Paul Jun from CreativeMornings/NewYork. Bottom left: Mark Lobo from CreativeMornings/Melbourne. Bottom right: Rachel Jolley from CreativeMornings/StPete.\r\n\r\n\u003Cimg src=\"https://mcusercontent.com/b8d0c821758ac4979998d6301/images/f4dba2f3-117c-8d6f-dada-a797cc6e2178.png\"\u003E\u003Cbr /\u003E\r\nFor full details about hosting a CreativeMornings chapter in your city, review our \u003Ca href=\"https://creativemornings.com/pages/start-a-chapter\" target=\"new\"\u003EStart a Chapter\u003C/a\u003E page and start your journey there!\r\n\r\n",
      //   "available_ticket_count": 0
      // }]
      axios.get("/talks/upcoming.json").then((response) => {
        this.events = response.data;
      });
    },
    getCoords(lat = 0, lng = 0) {
      let longitude_shift = 53, // number of pixels your map's prime meridian is off-center.
        x_pos = 54,
        y_pos = 19,
        map_width = this.width,
        map_height = this.height + 53; // larger than 130 because we're cutting off antartica.

      let x = (((map_width * (180 + lng)) / 360) % map_width) + longitude_shift;

      // latitude: using the Mercator projection
      lat = (lat * Math.PI) / 180; // convert from degrees to radians
      let y = Math.log(Math.tan(lat / 2 + Math.PI / 4));
      y = map_height / 2 - (map_width * y) / (2 * Math.PI) + y_pos;

      x -= x_pos;
      y -= y_pos;

      return { x, y };
    },
  },
};
</script>

<style lang="scss" scoped>
.cities-map {
  background: var(--bg-2);
  svg {
    display: block;
    width: 100%;
    height: 100%;
    max-height: 30rem;
  }
  &__landmass {
    fill: var(--bg);
  }
  a {
    vector-effect: non-scaling-stroke;
  }
  &__popup {
    pointer-events: none;
  }
  &__triangle {
  }
  text {
    font-size: 4px;
    text-anchor: middle;
    fill: var(--bg);
    font-weight: 700;
  }
}
</style>



// WEBPACK FOOTER //
// app/javascript/components/cities-map.vue?94b5ccda